import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { StatCard } from '@/components/customer-portal/stat-card';
import { SpendingChart } from '@/components/customer-portal/spending-chart';
import { InvoiceList } from '@/components/customer-portal/invoice-list';
import {
  CreditCard,
  Calendar,
  DollarSign,
  FileText,
  ArrowUpRight,
  AlertCircle,
} from 'lucide-react';

// Mock data for the spending chart
const spendingData = [
  { name: 'Jan', value: 1200 },
  { name: 'Feb', value: 1500 },
  { name: 'Mar', value: 1300 },
  { name: 'Apr', value: 1800 },
  { name: 'May', value: 1600 },
  { name: 'Jun', value: 2000 },
];

// Mock data for invoices
const mockInvoices = [
  {
    id: 'INV-001',
    number: 'INV-2024-001',
    date: '2024-01-01',
    dueDate: '2024-01-15',
    amount: 1500.00,
    status: 'paid',
  },
  {
    id: 'INV-002',
    number: 'INV-2024-002',
    date: '2024-02-01',
    dueDate: '2024-02-15',
    amount: 1650.00,
    status: 'pending',
  },
];

export function CustomerPortalPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filteredInvoices = mockInvoices.filter((invoice) => {
    const matchesSearch = invoice.number.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleDownload = (invoiceId: string) => {
    console.log('Downloading invoice:', invoiceId);
  };

  const totalSpent = mockInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const pendingAmount = mockInvoices
    .filter(inv => inv.status === 'pending')
    .reduce((sum, inv) => sum + inv.amount, 0);
  const overdueAmount = mockInvoices
    .filter(inv => inv.status === 'overdue')
    .reduce((sum, inv) => sum + inv.amount, 0);

  return (
    <div className="space-y-8">
      {/* Alert for overdue payments */}
      {overdueAmount > 0 && (
        <div className="flex items-center gap-2 p-4 rounded-lg bg-destructive/10 text-destructive">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span className="truncate">You have ${overdueAmount.toLocaleString()} in overdue payments</span>
          <Button variant="link" className="text-destructive shrink-0 ml-auto">
            View Details
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Spent"
          value={`$${totalSpent.toLocaleString()}`}
          icon={DollarSign}
          trend={{
            value: "+12%",
            icon: ArrowUpRight,
            color: "text-green-600"
          }}
        />

        <StatCard
          title="Pending Payments"
          value={`$${pendingAmount.toLocaleString()}`}
          icon={FileText}
          iconBgColor="bg-yellow-500/10"
          iconColor="text-yellow-500"
          badge={{
            text: "2 invoices",
            variant: "outline"
          }}
        />

        <StatCard
          title="Next Payment Due"
          value="Feb 15, 2024"
          icon={Calendar}
          badge={{
            text: "5 days left",
            variant: "default"
          }}
        />

        <StatCard
          title="Payment Method"
          value="•••• 4242"
          subValue="Expires 04/25"
          icon={CreditCard}
          badge={{
            text: "Visa",
            variant: "outline"
          }}
        />
      </div>

      {/* Spending Chart */}
      <div className="overflow-hidden">
        <SpendingChart data={spendingData} />
      </div>

      {/* Invoices List */}
      <div className="overflow-hidden">
        <InvoiceList
// @ts-ignore - TODO: Fix this TypeScript error
          invoices={filteredInvoices}
          search={search}
          statusFilter={statusFilter}
          onSearchChange={setSearch}
          onStatusChange={setStatusFilter}
          onDownload={handleDownload}
        />
      </div>
    </div>
  );
}